import { ADMIN_API_BASE_URL, currentAppMode } from "../config";
import { handleApiResponse } from "./commonfunction";

export interface ApiRequestOptions extends RequestInit {
  includeAppType?: boolean;
}

/**
 * Centralized API helper that automatically adds appType to requests
 * @param endpoint - API endpoint (without base URL)
 * @param options - Fetch options
 * @returns Promise with API response
 */
export async function makeApiRequest(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<any> {
  const { includeAppType = true, ...fetchOptions } = options;

  // Get auth headers
  const token = localStorage.getItem("adminToken");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...fetchOptions.headers,
  };

  // Add appType to request
  if (includeAppType) {
    const appType = currentAppMode;

    // If request has a body (POST, PUT, PATCH), add appType to body
    if (fetchOptions.body && fetchOptions.method !== "GET") {
      try {
        const bodyData =
          typeof fetchOptions.body === "string"
            ? JSON.parse(fetchOptions.body)
            : fetchOptions.body;
        fetchOptions.body = JSON.stringify({
          ...bodyData,
          appType,
        });
      } catch (error) {
        console.warn(
          "Failed to parse request body for appType injection:",
          error
        );
        // If body parsing fails, add appType as query parameter
        const url = new URL(endpoint, ADMIN_API_BASE_URL);
        url.searchParams.set("appType", appType);
        endpoint = url.pathname + url.search;
      }
    } else {
      // For GET requests or requests without body, add appType as query parameter
      const url = new URL(endpoint, ADMIN_API_BASE_URL);
      url.searchParams.set("appType", appType);
      endpoint = url.pathname + url.search;
    }
  }

  const response = await fetch(`${ADMIN_API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  return handleApiResponse(response);
}

/**
 * Helper function to add appType to existing URLSearchParams
 * @param params - URLSearchParams object
 * @returns URLSearchParams with appType added
 */
export function addAppTypeToParams(params: URLSearchParams): URLSearchParams {
  const newParams = new URLSearchParams(params);
  newParams.set("appType", currentAppMode);
  return newParams;
}

/**
 * Helper function to add appType to request body
 * @param bodyData - Request body data
 * @returns Body data with appType added
 */
export function addAppTypeToBody(bodyData: any): any {
  return {
    ...bodyData,
    appType: currentAppMode,
  };
}
